import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { finalize } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";
import { EventService } from "../event-service/event.service";
import { HubspotChatService } from "../hubspot-chat/hubspot-chat.service";
import { AccountService } from "../models/account.service";
import { UserService } from "../models/user.service";
import { InstructionsModalService } from "../project-detail/instructions-modal/instructions-modal.service";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { SendDataService } from "../models/send-data.service";
import { SendDataModalService } from "../send-data-modal/send-data-modal.service";
import { ShortcutsModalService } from "../shortcuts-modal/shortcuts-modal.service";
import { TrackerService } from "../tracker/tracker.service";
import { RememberService } from "../remember/remember.service";
import { ColorModeService } from "../models/color-mode.service";

export const LOCATION_TOKEN = new InjectionToken<Location>("Window location");

@Component({
  selector: "ab-workbench",
  templateUrl: "./workbench.component.html",
  providers: [
    // Use our own provider for window.location to make it mockable in specs.
    { provide: LOCATION_TOKEN, useValue: window.location },
  ],
})
export class WorkbenchComponent implements OnDestroy, OnInit {
  @HostBinding("class") classAttribute = "d-flex flex-column";
  @Input() public project: any;
  @Input() public projects: any;
  @Input() public user: any;

  public checklistVisibilitySub: any;
  public indicatorVisibilitySub: any;
  public linkSelectedSub: any;
  public menuOpened: boolean;
  public showIndicator: boolean;
  public trialDaysRemaining: number;
  public unconvertedTrial: boolean;
  public onFreeDevPlan: boolean;
  public showUpgradeCta: boolean;
  public currentTheme: string;
  public showInstallAirbrakeButton: boolean;
  public showInstallAirbrakeBtnSub: { unsubscribe: () => void };
  public showSendDataButton: boolean;
  public showSendDataBtnSub: { unsubscribe: () => void };
  public demoModeUntilDate: string;
  public stoppingSendData: boolean;

  constructor(
    @Inject(LOCATION_TOKEN) private location: Location,
    public $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private accountService: AccountService,
    private authService: AuthService,
    private changeDetector: ChangeDetectorRef,
    private eventService: EventService,
    private hubspotChatService: HubspotChatService,
    private instructionsModalService: InstructionsModalService,
    private sendDataModalService: SendDataModalService,
    private shortcutsModalService: ShortcutsModalService,
    private trackerService: TrackerService,
    private userService: UserService,
    private sendDataService: SendDataService,
    private rememberService: RememberService,
    private colorModeService: ColorModeService,
  ) {}

  public logout() {
    this.authService.logout();
  }

  public showShortcutsModal() {
    this.shortcutsModalService.toggle();
  }

  public onProjectPage() {
    // The onboarding checklist is specific to a project, so we only want to
    // show the "Set up your Airbrake" link if we're on a project page.
    return this.$uiRouterGlobals.params.projectId != null;
  }

  public getCurrentProjectId() {
    return this.$uiRouterGlobals.params.projectId;
  }

  public showOnboardingChecklist() {
    this.closeMenu();
    this.updateChecklistVisibility(true);
    this.showIndicator = false;
  }

  public closeMenu() {
    this.menuOpened = false;
  }

  public updateIndicatorVisibility(show: boolean) {
    this.showIndicator = show;
  }

  public updateChecklistVisibility(show: boolean) {
    this.eventService.publish("show-checklist", show);
  }

  public showInstructionsModal() {
    this.trackerService.track("installAirbrakeButtonClicked");
    this.showInstallAirbrakeButton = false;
    this.instructionsModalService.open();
  }

  public showSendDataModal() {
    this.eventService.publish("show-send-data-btn", false);
    this.sendDataModalService.open();
  }

  public showStopSendingDataButton() {
    const project = this.currentProject();
    if (!project || project.demoModeUntilDate === null) {
      return false;
    }

    this.demoModeUntilDate = project.demoModeUntilDate;
    return new Date() < new Date(project.demoModeUntilDate);
  }

  public stopSendingData() {
    this.stoppingSendData = true;
    this.sendDataService
      .delete({ projectId: this.$uiRouterGlobals.params.projectId })
      .pipe(
        finalize(() => {
          const project = this.currentProject();
          project.demoModeUntilDate = null;
          this.stoppingSendData = false;
        }),
      )
      .subscribe();
  }

  public changeColorMode(colorMode: string) {
    this.colorModeService.switchMode(colorMode).subscribe(() => {
      window.location.reload();
    });
  }

  public ngOnInit() {
    this.projects ||= [];

    this.userService.properties().subscribe((props: any) => {
      // Users that are required to enable two-factor authentication should have
      // blocked access to all `airbrake-ng` pages until they enable it.
      if (props.should_enable_two_factor_auth) {
        this.location.replace(`/users/${this.user.id}/edit`);
        return;
      }
      if (props.hubspot_visitor_token) {
        this.hubspotChatService.identifyVisitor(
          this.user.email,
          props.hubspot_visitor_token,
        );
      }

      if (props.color_mode === "dark") {
        this.currentTheme = "dark";
      } else {
        if (props.color_mode === "system_default") {
          if (
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
          ) {
            this.currentTheme = "dark";
          } else {
            this.currentTheme = "light";
          }
        } else {
          this.currentTheme = "light";
        }
      }
    });

    this.accountService
      .properties()
      .subscribe((props: any) => {
        // Disallow access for restricted accounts.
        if (props.account.restricted) {
          this.location.replace("/account/credit_card/edit");
          return;
        }

        this.trialDaysRemaining = props.account.trial_days_remaining;
        this.unconvertedTrial = props.account.unconverted_trial;
        this.onFreeDevPlan = props.account.on_free_dev_plan;
        this.showUpgradeCta = this.unconvertedTrial || this.onFreeDevPlan;
      });

    this.menuOpened = false;
    this.showIndicator = false;
    this.hubspotChatService.load();
    this.stoppingSendData = false;

    const project = this.currentProject();
    if (
      this.rememberService.get("sendDataModalDismissed") &&
      project &&
      !project.demoModeUntilDate
    ) {
      this.showSendDataButton = true;
    }

    this.linkSelectedSub = this.eventService.subscribe(
      "project-nav:linkSelected",
      () => {
        this.closeMenu();
        this.showInstallAirbrakeButton = false;
      },
    );
    this.checklistVisibilitySub = this.eventService.subscribe(
      "checklist-visibility",
      (show: boolean) => {
        this.updateChecklistVisibility(show);
      },
    );
    this.indicatorVisibilitySub = this.eventService.subscribe(
      "indicator-visibility",
      (show: boolean) => {
        this.updateIndicatorVisibility(show);
      },
    );
    this.showInstallAirbrakeBtnSub = this.eventService.subscribe(
      "show-install-airbrake-btn",
      (show: boolean) => {
        this.showInstallAirbrakeButton = show;
        this.changeDetector.detectChanges();
      },
    );
    this.showSendDataBtnSub = this.eventService.subscribe(
      "show-send-data-btn",
      (show: boolean) => {
        this.rememberService.set("sendDataModalDismissed", show);
        this.showSendDataButton = show;
        this.changeDetector.detectChanges();
      },
    );
  }

  public ngOnDestroy() {
    this.linkSelectedSub.unsubscribe();
    this.checklistVisibilitySub.unsubscribe();
    this.indicatorVisibilitySub.unsubscribe();
    this.showInstallAirbrakeBtnSub.unsubscribe();
    this.showSendDataBtnSub.unsubscribe();
  }

  private currentProject() {
    const projectId = parseInt(this.$uiRouterGlobals.params.projectId, 10);
    return this.projects.find((p: any) => p.id === projectId);
  }
}
